import { useState } from 'react';
import type { GuaranteedServiceSkuEntry } from '@nadoutong/contracts';
import { useT } from '../../i18n/index.ts';
import { cx, Input } from '../../ui/index.ts';

/**
 * Column tracks shared by the header row in ConfigCards and every SKU row,
 * so columns line up: SKU | probe | chain | Jev | fallback | guard | 24h | delete.
 * The guard column (#3135) is the home for SKU-layer 保障配置. Minimum track
 * width ≈ 1120 px; narrower panels scroll horizontally inside ConfigCards.
 */
export const SKU_GRID = 'grid grid-cols-[minmax(130px,1.1fr)_minmax(250px,1.7fr)_minmax(100px,0.7fr)_minmax(162px,0.9fr)_minmax(100px,0.7fr)_minmax(168px,1.1fr)_128px_28px] gap-x-2.5';

function countOf(value: number | null | undefined): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

/** 「deepseek/」 dimmed, the model bright — the namespace is already the group header above. */
export function SkuName({ name }: { readonly name: string }): JSX.Element {
  const slash = name.indexOf('/');
  return (
    <div className="break-words text-body font-medium text-paper" title={name}>
      {slash < 0 ? name : (
        <>
          <span className="text-paper-low">{name.slice(0, slash + 1)}</span>
          <wbr />
          {name.slice(slash + 1)}
        </>
      )}
    </div>
  );
}

/** 24h counters as a right-aligned label / number ladder. */
export function RowStats({ row }: { readonly row: GuaranteedServiceSkuEntry }): JSX.Element {
  const t = useT();
  const failed = countOf(row.chain_exhausted_count_24h);
  const shadow = countOf(row.shadow_enter_24h);
  const lines: ReadonlyArray<readonly [string, number, string]> = [
    [t('guaranteed.stat.requests'), countOf(row.request_count_24h), 'text-paper'],
    [t('guaranteed.stat.retry'), countOf(row.entered_chain_count_24h), 'text-paper'],
    [t('guaranteed.stat.failed'), failed, failed > 0 ? 'text-bad' : 'text-paper'],
    [t('guaranteed.stat.shadow'), shadow, shadow > 0 ? 'text-warn' : 'text-paper'],
  ];
  return (
    <dl
      className="m-0 grid grid-cols-[1fr_auto] items-baseline gap-x-2.5 gap-y-1 pt-0.5 text-label"
      title={t('guaranteed.stats_hint')}
    >
      {lines.map(([label, value, tone]) => (
        <div key={label} className="contents">
          <dt className="truncate text-right text-paper-low">{label}</dt>
          <dd className={cx('m-0 text-right font-mono tabular-nums', tone)}>
            <span>{value.toLocaleString('en-US')}</span>
          </dd>
        </div>
      ))}
    </dl>
  );
}

export function CommitNumber({
  value,
  disabled,
  testId,
  label,
  validate,
  onCommit,
}: {
  readonly value: number;
  readonly disabled: boolean;
  readonly testId: string;
  readonly label: string;
  readonly validate: (value: number) => boolean;
  readonly onCommit: (value: number) => void;
}): JSX.Element {
  const [draft, setDraft] = useState<string | null>(null);
  const shown = draft ?? String(value);
  return (
    <Input
      compact
      wrapperClassName="w-11"
      className="text-right"
      mono
      value={shown}
      disabled={disabled}
      inputMode="numeric"
      aria-label={label}
      data-testid={testId}
      onChange={(event) => setDraft(event.currentTarget.value)}
      onBlur={() => {
        const parsed = Number(shown);
        setDraft(null);
        if (!validate(parsed) || parsed === value) return;
        onCommit(parsed);
      }}
    />
  );
}
